import { useEffect, useRef, useState } from "react";
import * as catalogoApi from "../../api/catalogo";
import { DispositivoCatalogo } from "../../api/catalogo";
import { TipoDispositivo } from "../../types";
import { Input } from "./Input";

interface DispositivoAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Chiamato quando l'utente sceglie un suggerimento dal Catalogo Dispositivi —
      il chiamante lo usa per precompilare marca/dispositivo_catalogo_id e per
      mostrare subito la documentazione collegata (DispositivoMateriale). */
  onSelect?: (dispositivo: DispositivoCatalogo) => void;
  /** Se indicato, limita i suggerimenti a questa tipologia (es. "Cassaforte"). */
  tipoDispositivo?: TipoDispositivo | "";
  placeholder?: string;
}

/**
 * Suggerisce modelli già censiti nel Catalogo Dispositivi mentre si scrive,
 * filtrando su marca/modello/sigla — il catalogo è un elenco precaricato di
 * modelli (non di istanze), quindi lo carichiamo una volta e filtriamo lato
 * client invece di interrogare il backend a ogni tasto, come invece serve per
 * ClienteAutocomplete/SedeAutocomplete che cercano tra migliaia di record.
 */
export function DispositivoAutocomplete({
  label,
  value,
  onChange,
  onSelect,
  tipoDispositivo,
  placeholder,
}: DispositivoAutocompleteProps) {
  const [catalogo, setCatalogo] = useState<DispositivoCatalogo[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    catalogoApi
      .listCatalogo(tipoDispositivo || undefined)
      .then(({ dispositivi }) => setCatalogo(dispositivi))
      .catch(() => setCatalogo([]));
  }, [tipoDispositivo]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const query = value.trim().toLowerCase();
  const suggestions =
    query.length < 1
      ? []
      : catalogo
          .filter((d) => `${d.marca} ${d.modello} ${d.sigla ?? ""}`.toLowerCase().includes(query))
          .slice(0, 8);

  function handleSelect(dispositivo: DispositivoCatalogo) {
    onChange(dispositivo.modello);
    onSelect?.(dispositivo);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-white shadow-lg">
          {suggestions.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => handleSelect(d)}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-primary/5"
            >
              <span className="font-medium text-ink">
                {d.marca} {d.modello}
              </span>
              <span className="text-xs text-ink/50">
                {d.tipo_dispositivo}
                {d.sigla && ` · Sigla ${d.sigla}`}
                {d.documenti.length > 0 && ` · ${d.documenti.length} documenti`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
